/**
 * Created: 2026-04-08
 * Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
 * Updated: 2026-04-12 — replaced DataGridPro with pure CSS Grid (feat/event/mr-14)
 *
 * Slot grid management page — orchestrates date selection, queries, drag
 * selection, slot actions, and dialogs.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material'
import { format, isBefore, isToday } from 'date-fns'
import { useMemo, useState } from 'react'
import DateNavigation from '../../components/DateNavigation'
import { AdminSitesDocument, SlotStatus, SlotsDocument } from '../../gql/graphql'
import { useSiteContext } from '../../hooks'
import CreateEventDialog from '../CreateEventDialog'
import EventDialog from '../EventDialog'
import OrderDialog from '../OrderDialog'
import SlotGrid, { type SpanBlock } from './SlotGrid'
import styles from './SlotGrid.module.css'
import type { SlotMeta } from './types'
import { useDragSelection } from './useDragSelection'
import { useSlotActions } from './useSlotActions'

gql`
  query Slots($siteId: UUID!, $date: Date!) {
    slots(siteId: $siteId, date: $date) {
      courtId
      startTime
      price
      status
      reason
      orderId
      eventId
    }
  }
`

/** Current hour in China timezone (0-23) */
function getChinaHour() {
  return (new Date().getUTCHours() + 8) % 24
}

/** Today's date in China timezone as a Date (midnight local) */
function getChinaToday() {
  const now = new Date()
  const chinaMs = now.getTime() + (8 * 60 + now.getTimezoneOffset()) * 60_000
  const china = new Date(chinaMs)
  return new Date(china.getFullYear(), china.getMonth(), china.getDate())
}

const STATUS_LEGEND: {
  status: SlotStatus
  label: string
  bg: string
  color: string
}[] = [
  { status: SlotStatus.Open, label: '可预订', bg: '#fff', color: '#333' },
  { status: SlotStatus.Reserved, label: '已保留', bg: '#fff3e0', color: '#e65100' },
  { status: SlotStatus.Booked, label: '已预订', bg: '#e8f5e9', color: '#2e7d32' },
  { status: SlotStatus.Locked, label: '已锁定', bg: '#e0e0e0', color: '#424242' },
]

export default function SlotsPage() {
  const { currentSite } = useSiteContext()
  const [selectedDate, setSelectedDate] = useState<Date>(new Date())

  const {
    data: sitesData,
    loading: sitesLoading,
    error: sitesError,
  } = useQuery(AdminSitesDocument, {
    fetchPolicy: 'network-only',
  })

  const {
    data: slotsData,
    loading: slotsLoading,
    error: slotsError,
    refetch: slotsRefetch,
  } = useQuery(SlotsDocument, {
    variables: {
      siteId: currentSite?.id as string,
      date: format(selectedDate, 'yyyy-MM-dd'),
    },
    skip: !currentSite,
    fetchPolicy: 'no-cache',
  })

  const courts = useMemo(() => {
    if (!sitesData || !currentSite) return []
    const site = sitesData.adminSites.find((s) => s.id === currentSite.id)
    const courtsList = site?.courts ?? []
    return [...courtsList].sort((a, b) => a.displayOrder - b.displayOrder)
  }, [sitesData, currentSite])

  const { slotLookup, hours } = useMemo(() => {
    const slots = slotsData?.slots ?? []
    const lookup = new Map<string, SlotMeta>()
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const s of slots) {
      const chinaHour = (new Date(s.startTime).getUTCHours() + 8) % 24
      lookup.set(`${chinaHour}-${s.courtId}`, {
        price: s.price ?? '',
        status: s.status,
        startTime: s.startTime,
        reason: s.reason ?? undefined,
        orderId: s.orderId ?? undefined,
        eventId: s.eventId ?? undefined,
      })
      min = Math.min(min, chinaHour)
      max = Math.max(max, chinaHour)
    }
    const hourStart = min === Number.POSITIVE_INFINITY ? 0 : min
    const hourEnd = max === Number.NEGATIVE_INFINITY ? 0 : max
    return {
      slotLookup: lookup,
      hours: Array.from({ length: hourEnd - hourStart + 1 }, (_, i) => hourStart + i),
    }
  }, [slotsData])

  // Compute span blocks (merged cells), covered cells, and span maps for drag selection.
  // Merges both vertically (consecutive hours, same court) and horizontally (adjacent courts)
  // when cells share the same orderId, eventId, or reason.
  const { spanBlocks, coveredCells, spanKeyMap, spanGroupCells } = useMemo(() => {
    const blocks: SpanBlock[] = []
    const covered = new Set<string>()
    const keyMap = new Map<string, string>()
    const grpCells = new Map<string, { hour: number; courtId: string }[]>()

    // Phase 1: Group cells by orderId / eventId → bounding rectangle span
    const idBuckets = new Map<
      string,
      { courtIndex: number; hour: number; courtId: string }[]
    >()
    for (const [ci, court] of courts.entries()) {
      for (const hour of hours) {
        const meta = slotLookup.get(`${hour}-${court.id}`)
        if (!meta) continue
        const id = meta.orderId
          ? `order:${meta.orderId}`
          : meta.eventId
            ? `event:${meta.eventId}`
            : null
        if (!id) continue
        if (!idBuckets.has(id)) idBuckets.set(id, [])
        idBuckets.get(id)?.push({ courtIndex: ci, hour, courtId: court.id })
      }
    }

    let p1RunCounter = 0
    for (const [id, cells] of idBuckets) {
      if (cells.length === 0) continue

      // Sort unique court indices to find contiguous runs — a gap means two courts
      // that share an orderId/eventId but are not adjacent (e.g. court 1 and court 6)
      // must NOT be merged into a single span that would wrongly cover the courts in
      // between. Each contiguous run becomes its own span block (or is left as an
      // individual cell when rowSpan=1, colSpan=1).
      const uniqueCIs = [...new Set(cells.map((c) => c.courtIndex))].sort(
        (a, b) => a - b,
      )

      let i = 0
      while (i < uniqueCIs.length) {
        // Extend the run as long as the next court index is immediately adjacent
        let j = i
        while (j + 1 < uniqueCIs.length && uniqueCIs[j + 1] === uniqueCIs[j] + 1) j++

        const minCI = uniqueCIs[i]
        const colSpan = j - i + 1
        const ciSet = new Set(uniqueCIs.slice(i, j + 1))

        const runCells = cells.filter((c) => ciSet.has(c.courtIndex))
        const runHours = runCells.map((c) => c.hour)
        const minH = Math.min(...runHours)
        const maxH = Math.max(...runHours)
        const rowSpan = maxH - minH + 1

        if (rowSpan > 1 || colSpan > 1) {
          const spanKey = `${id}-${++p1RunCounter}`
          const cellList = runCells.map((c) => ({ hour: c.hour, courtId: c.courtId }))
          for (const c of cellList) {
            keyMap.set(`${c.hour}-${c.courtId}`, spanKey)
            covered.add(`${c.hour}-${c.courtId}`)
          }
          grpCells.set(spanKey, cellList)
          const meta = slotLookup.get(`${minH}-${courts[minCI].id}`)
          if (meta)
            blocks.push({ courtIndex: minCI, hourStart: minH, rowSpan, colSpan, meta })
        }

        i = j + 1
      }
    }

    // Phase 2: Reason-only cells — build vertical runs per court, then merge adjacent courts
    // whose runs have identical (hourStart, hourEnd, reason).
    const reasonRuns: {
      ci: number
      courtId: string
      hourStart: number
      hourEnd: number
      reason: string
    }[] = []

    for (const [ci, court] of courts.entries()) {
      let runStart: number | null = null
      let runReason: string | undefined

      for (const hour of hours) {
        const cellKey = `${hour}-${court.id}`
        if (covered.has(cellKey)) {
          if (runStart !== null && runReason !== undefined) {
            reasonRuns.push({
              ci,
              courtId: court.id,
              hourStart: runStart,
              hourEnd: hour - 1,
              reason: runReason,
            })
            runStart = null
            runReason = undefined
          }
          continue
        }
        const reason = slotLookup.get(cellKey)?.reason
        if (reason !== undefined && reason === runReason) {
          // extend existing run
        } else {
          if (runStart !== null && runReason !== undefined) {
            reasonRuns.push({
              ci,
              courtId: court.id,
              hourStart: runStart,
              hourEnd: hour - 1,
              reason: runReason,
            })
          }
          runStart = reason !== undefined ? hour : null
          runReason = reason
        }
      }
      if (runStart !== null && runReason !== undefined) {
        reasonRuns.push({
          ci,
          courtId: court.id,
          hourStart: runStart,
          hourEnd: hours[hours.length - 1],
          reason: runReason,
        })
      }
    }

    // Group runs by (hourStart, hourEnd, reason), then find contiguous court sequences
    const runBuckets = new Map<string, typeof reasonRuns>()
    for (const run of reasonRuns) {
      const k = `${run.hourStart}:${run.hourEnd}:${run.reason}`
      if (!runBuckets.has(k)) runBuckets.set(k, [])
      runBuckets.get(k)?.push(run)
    }

    let rgc = 0
    for (const [, runs] of runBuckets) {
      const sorted = [...runs].sort((a, b) => a.ci - b.ci)
      let i = 0
      while (i < sorted.length) {
        let j = i
        while (j + 1 < sorted.length && sorted[j + 1].ci === sorted[j].ci + 1) j++
        const sub = sorted.slice(i, j + 1)
        const colSpan = sub.length
        const { ci: minCI, hourStart, hourEnd } = sub[0]
        const rowSpan = hourEnd - hourStart + 1

        const spanKey = `rg-${++rgc}`
        const cellList: { hour: number; courtId: string }[] = []

        for (const run of sub) {
          for (let h = run.hourStart; h <= run.hourEnd; h++) {
            const cellKey = `${h}-${run.courtId}`
            keyMap.set(cellKey, spanKey)
            cellList.push({ hour: h, courtId: run.courtId })
            if (rowSpan > 1 || colSpan > 1) covered.add(cellKey)
          }
        }

        if (rowSpan > 1 || colSpan > 1) {
          const meta = slotLookup.get(`${hourStart}-${courts[minCI].id}`)
          if (meta)
            blocks.push({ courtIndex: minCI, hourStart, rowSpan, colSpan, meta })
          grpCells.set(spanKey, cellList)
        }

        i = j + 1
      }
    }

    return {
      spanBlocks: blocks,
      coveredCells: covered,
      spanKeyMap: keyMap,
      spanGroupCells: grpCells,
    }
  }, [courts, slotLookup, hours])

  const pastHourThreshold = useMemo(() => {
    const chinaToday = getChinaToday()
    if (isBefore(selectedDate, chinaToday)) return 24
    if (
      isToday(chinaToday) &&
      format(selectedDate, 'yyyy-MM-dd') === format(chinaToday, 'yyyy-MM-dd')
    )
      return getChinaHour() + 1
    return 0
  }, [selectedDate])

  const courtIds = useMemo(() => courts.map((c) => c.id), [courts])

  const {
    selectedCells,
    setSelectedCells,
    selectedKeys,
    gridRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
  } = useDragSelection({
    courtIds,
    pastHourThreshold,
    slotLookup,
    spanKeyMap,
    spanGroupCells,
    dragHighlightClass: styles.dragHighlight,
  })

  const refetchSlots = () => {
    if (!currentSite) return
    return slotsRefetch()
  }

  const {
    hasOpen,
    hasReserved,
    canCreateEvent,
    eventSlots,
    mutating,
    reasonDialogOpen,
    setReasonDialogOpen,
    reasonInput,
    setReasonInput,
    priceDialogOpen,
    setPriceDialogOpen,
    priceInput,
    setPriceInput,
    handleReserveOpen,
    handleReserveConfirm,
    handleRelease,
    handleChangePriceOpen,
    handleChangePriceConfirm,
  } = useSlotActions({
    selectedCells,
    setSelectedCells,
    slotLookup,
    courts,
    slotsRefetch: refetchSlots,
  })

  // Dialog state
  const [orderDialogId, setOrderDialogId] = useState<string | null>(null)
  const [eventDialogId, setEventDialogId] = useState<string | null>(null)
  const [createEventOpen, setCreateEventOpen] = useState(false)

  const loading = sitesLoading || slotsLoading
  const error = sitesError || slotsError

  const handleCellClick = (hour: number, courtId: string) => {
    const meta = slotLookup.get(`${hour}-${courtId}`)
    if (meta?.status === SlotStatus.Booked && meta.orderId) {
      setOrderDialogId(meta.orderId)
    } else if (meta?.eventId) {
      setEventDialogId(meta.eventId)
    }
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 48px)',
      }}
    >
      {/* Header: title + date navigation */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
        }}
      >
        <Typography variant="h4">场地管理</Typography>
        <DateNavigation value={selectedDate} onChange={setSelectedDate} />
      </Box>

      {/* Action buttons + legend */}
      <Box
        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}
      >
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasOpen || mutating}
            onClick={handleReserveOpen}
          >
            保留
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasReserved || mutating}
            onClick={handleRelease}
          >
            释放
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!canCreateEvent || mutating}
            onClick={() => setCreateEventOpen(true)}
          >
            活动
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={!hasOpen || mutating}
            onClick={handleChangePriceOpen}
          >
            改价
          </Button>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto' }}>
          {STATUS_LEGEND.map((item) => (
            <Box
              key={item.status}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                fontSize: '0.75rem',
              }}
            >
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '2px',
                  backgroundColor: item.bg,
                  border: '1px solid #ccc',
                }}
              />
              <Typography variant="caption" sx={{ color: item.color }}>
                {item.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {error && <Alert severity="error">加载预约数据失败: {error.message}</Alert>}

      <SlotGrid
        courts={courts}
        hours={hours}
        slotLookup={slotLookup}
        coveredCells={coveredCells}
        spanBlocks={spanBlocks}
        pastHourThreshold={pastHourThreshold}
        selectedKeys={selectedKeys}
        loading={loading}
        gridRef={gridRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onCellClick={handleCellClick}
      />

      {/* Reserve reason dialog */}
      <Dialog
        open={reasonDialogOpen}
        onClose={() => setReasonDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>保留原因</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="原因"
            fullWidth
            multiline
            rows={3}
            value={reasonInput}
            onChange={(e) => setReasonInput(e.target.value.slice(0, 100))}
            slotProps={{
              htmlInput: { maxLength: 100 },
            }}
            helperText={`${reasonInput.length}/100`}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReasonDialogOpen(false)}>取消</Button>
          <Button onClick={handleReserveConfirm} variant="contained">
            确认
          </Button>
        </DialogActions>
      </Dialog>

      {/* Change price dialog */}
      <Dialog
        open={priceDialogOpen}
        onClose={() => setPriceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>修改价格</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            label="新价格 (元)"
            type="number"
            fullWidth
            value={priceInput}
            onChange={(e) => {
              const v = e.target.value
              if (v.includes('-')) return
              if (v === '' || (/^\d{0,3}(\.\d{0,2})?$/.test(v) && Number(v) <= 999)) {
                setPriceInput(v)
              }
            }}
            onKeyDown={(e) => {
              if (e.key === '-' || e.key === 'Minus') {
                e.preventDefault()
              }
            }}
            slotProps={{
              htmlInput: { min: 0, max: 999, step: 0.01 },
            }}
            sx={{
              mt: 1,
              '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button':
                {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceDialogOpen(false)}>取消</Button>
          <Button
            onClick={handleChangePriceConfirm}
            variant="contained"
            disabled={
              priceInput === '' || Number(priceInput) < 0 || Number(priceInput) > 999
            }
          >
            确认
          </Button>
        </DialogActions>
      </Dialog>

      <OrderDialog orderId={orderDialogId} onClose={() => setOrderDialogId(null)} />

      <EventDialog
        eventId={eventDialogId}
        onClose={() => {
          setEventDialogId(null)
          refetchSlots()
        }}
      />

      {createEventOpen && (
        <CreateEventDialog
          open
          slots={eventSlots}
          onClose={() => setCreateEventOpen(false)}
          onSuccess={() => {
            setCreateEventOpen(false)
            setSelectedCells([])
            refetchSlots()
          }}
        />
      )}
    </Box>
  )
}
